import { OrgId, View } from "../types";

// URL scheme — each page has its own route. Global views (not org-specific)
// sit at the root; org-scoped views carry the org as the first path segment,
// mirroring the sidebar's global-above / org-below split.
//
//   /                  Home
//   /hub               Agents Hub          (overview of sessions, tasks, common)
//   /hub/sessions      Sessions
//   /hub/tasks         Tasks
//   /hub/common        Common
//   /hub/runtime       Runtime             (autonomous agent runtimes, admin-only)
//   /<org>/repos       Repositories
//   /<org>/docs        System Docs        (?doc=<path>  deep-links a document)
//   /<org>/graph       Knowledge Graph    (?node=<id|alias> deep-links a node)
//   /<org>/devhub                DevHub overview         (when corpus exists)
//   /<org>/devhub/docs           DevHub · Docs           (?open=<path>)
//   /<org>/devhub/api-reference  DevHub · API Reference  (?open=<path>)
//   /<org>/agents      Agents             (?repo=<name> deep-links a repo)
//   /<org>/mcp         MCP connections    (?name=<connection> selects one; admin-only)

export const ORGS: OrgId[] = [OrgId.Incode, OrgId.Metamap, OrgId.Aid];

export function isOrg(value: string): value is OrgId {
  return (ORGS as string[]).includes(value);
}

// DevHub route/preview URL helpers (splitDevhubPath, devhubTab*, preview*) moved
// to the DevHub module: apps/omni-hub-web/src/devhub/lib/routes.ts.

// Parse a pathname into the org (when the view is org-scoped) and the view.
// Unknown paths fall back to Home.
export function parseRoute(pathname: string): { org?: OrgId; view: View } {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return { view: View.Home };
  // Standalone public docs preview, opened in a new tab — has no app chrome.
  // /preview/<org>/<section>/<slug>
  if (parts[0] === "preview") return { view: View.Preview };
  if (parts[0] === "hub") {
    switch (parts[1]) {
      case "sessions": return { view: View.Sessions };
      case "tasks":    return { view: View.Tasks };
      case "common":   return { view: View.Skills };
      case "runtime":  return { view: View.Runtime };
      default:         return { view: View.Hub };
    }
  }
  // Omni Hub admin settings — a global (non-org) view.
  if (parts[0] === "settings") return { view: View.Settings };
  // Legacy aliases for the pre-nesting flat routes.
  if (parts[0] === "tasks") return { view: View.Tasks };
  if (parts[0] === "common") return { view: View.Skills };

  if (isOrg(parts[0])) {
    const org = parts[0];
    switch (parts[1]) {
      case "repos":
        return { org, view: View.Repos };
      case "docs":
        return { org, view: View.Docs };
      case "graph":
        return { org, view: View.Graph };
      case "devhub":
        // The DevHub page is one view; the tab (docs | api-reference | <section>)
        // is the path segment, read in DevHubOverview via devhubTabFromPath.
        return { org, view: View.Devhub };
      case "agents":
        return { org, view: View.Agents };
      case "mcp":
        return { org, view: View.Mcp };
      case "guides":
        // /<org>/guides[/<collection>[/<slug>]] — the open page is read from the
        // path in GuidesPage (guidePathParts).
        return { org, view: View.Guides };
      default:
        return { org, view: View.Home };
    }
  }

  return { view: View.Home };
}

// Build the path for a view in a given org.
export function pathFor(view: View, org: OrgId): string {
  switch (view) {
    case View.Home:
      return "/";
    case View.Hub:
      return "/hub";
    case View.Sessions:
      return "/hub/sessions";
    case View.Tasks:
      return "/hub/tasks";
    case View.Skills:
      return "/hub/common";
    case View.Runtime:
      return "/hub/runtime";
    case View.Settings:
      return "/settings";
    case View.Repos:
      return `/${org}/repos`;
    case View.Docs:
      return `/${org}/docs`;
    case View.Graph:
      return `/${org}/graph`;
    case View.Devhub:
      return `/${org}/devhub/home`; // default tab — the Home overview
    case View.Preview:
      // Preview targets a specific page; callers build the full path. This base
      // is only a fallback (the standalone view ignores it).
      return "/preview";
    case View.Agents:
      return `/${org}/agents`;
    case View.Mcp:
      return `/${org}/mcp`;
    case View.Guides:
      return `/${org}/guides`;
  }
}

// Omni Hub Settings is one global view (/settings); its section lives in the
// path (/settings/<section>) and is read here — mirroring how DevHub reads its
// tab from the path. parseRoute maps any /settings/* to View.Settings, so these
// sections need no new View. "account" is the default and sits at bare /settings.
export const SETTINGS_SECTIONS = ["account", "organizations", "security", "system", "users", "ai"] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
export function settingsSectionPath(section: SettingsSection): string {
  return section === "account" ? "/settings" : `/settings/${section}`;
}
export function settingsSectionFromPath(pathname: string): SettingsSection {
  const seg = pathname.split("/").filter(Boolean)[1]; // /settings/<section>
  return (SETTINGS_SECTIONS as readonly string[]).includes(seg ?? "") ? (seg as SettingsSection) : "account";
}
